import {DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {
  getModelTypeConstantForObjectType,
  getObjectTypesWithInterface,
} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {
  coreObjectArchiveAction,
  coreObjectUnarchiveAction,
} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {DataModificationsHeader} from 'audit/DataModifications';
import ModelTypeConstants, {STORABLE_ITEM_TYPES} from 'constants/ModelTypeConstants';
import APIUtil from 'util/APIUtil';

import {
  bulkArchiveConnections,
  BulkArchiveFilesOptions,
  bulkArchiveFilesWithDataMods,
  bulkArchiveSourcesWithDataMods,
  bulkArchiveTemplateCollections,
  bulkUnarchiveConnections,
  bulkUnarchiveFilesWithDataMods,
  bulkUnarchiveSourcesWithDataMods,
  bulkArchiveAnalyses,
  bulkUnarchiveAnalyses,
  bulkUnarchiveTemplateCollections,
} from './ArchiveActions';

type CoreObjectArchiveActionType = (
  ids: Array<string>,
  purpose: string,
  ignoredBulkArchiveFilesOptions?: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
) => void;

type LegacyArchiveActionType = (
  ids: Array<string>,
  purpose: string,
  bulkArchiveFilesOptions: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void
) => Promise<void>;

type LegacyArchiveWithDatamodsActionType = (
  ids: Array<string>,
  purpose: string,
  bulkArchiveFilesOptions: BulkArchiveFilesOptions
) => void;

type ArchiveActionType =
  | LegacyArchiveActionType
  | LegacyArchiveWithDatamodsActionType
  | CoreObjectArchiveActionType;

type UnarchiveActionType = (
  ids: Array<string>,
  unarchiveOptions?: {hasRegisteredItems: boolean},
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
) => Promise<void>;

interface ArchiveActionInfo {
  modelTypes: Array<ModelTypeConstants>;
  archive: ArchiveActionType;
  unarchive: UnarchiveActionType;
}

const CORE_ARCHIVABLE_TYPES = Array.from(
  getObjectTypesWithInterface(DomainGraphInterface.CORE_OBJECT_ARCHIVABLE)
).map((type) => getModelTypeConstantForObjectType(type));

const ARCHIVABLE_FILE_TYPES: Array<ModelTypeConstants> = [
  ...STORABLE_ITEM_TYPES,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.BATCH,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.FOLDER,
  ModelTypeConstants.FORM_DEFINITION,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.PROCESS_OUTPUT,
  ModelTypeConstants.PROCESS_TASK,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.SUBTEMPLATE,
  ModelTypeConstants.TEMPLATE,
];

const ITEM_TYPES_ARCHIVE_ACTION_INFO: Array<ArchiveActionInfo> = [
  {
    modelTypes: [ModelTypeConstants.SOURCE],
    archive: bulkArchiveSourcesWithDataMods,
    unarchive: bulkUnarchiveSourcesWithDataMods,
  },
  {
    modelTypes: ARCHIVABLE_FILE_TYPES,
    archive: bulkArchiveFilesWithDataMods,
    unarchive: bulkUnarchiveFilesWithDataMods,
  },
  {
    modelTypes: [ModelTypeConstants.CONNECTION],
    archive: bulkArchiveConnections,
    unarchive: bulkUnarchiveConnections,
  },
  {
    modelTypes: [ModelTypeConstants.TEMPLATE_COLLECTION],
    archive: bulkArchiveTemplateCollections,
    unarchive: bulkUnarchiveTemplateCollections,
  },
  {
    modelTypes: [ModelTypeConstants.ANALYSIS],
    archive: bulkArchiveAnalyses,
    unarchive: bulkUnarchiveAnalyses,
  },
  {
    modelTypes: CORE_ARCHIVABLE_TYPES,
    archive: coreObjectArchiveAction,
    unarchive: coreObjectUnarchiveAction,
  },
];

export function getArchiveActionInfoFromModelTypes(
  modelTypes: Array<ModelTypeConstants>
): ArchiveActionInfo | null {
  return (
    ITEM_TYPES_ARCHIVE_ACTION_INFO.find((info: ArchiveActionInfo) =>
      modelTypes.every((modelType) => info.modelTypes.includes(modelType))
    ) ?? null
  );
}

export function getArchiveAction(ids: Array<string>): ArchiveActionType {
  const actionInfo = getArchiveActionInfoFromModelTypes(ids.map((id) => APIUtil.apiIdToType(id)));
  if (!actionInfo) {
    throw new Error('Invalid ids for getArchiveAction');
  }
  return actionInfo.archive;
}

export function getUnarchiveAction(ids: Array<string>): UnarchiveActionType {
  const actionInfo = getArchiveActionInfoFromModelTypes(ids.map((id) => APIUtil.apiIdToType(id)));
  if (!actionInfo) {
    throw new Error('Invalid ids for getUnarchiveAction');
  }
  return actionInfo.unarchive;
}
